import { FunctionComponent } from 'react'
import ActionText from '../../../ActionText'
import { useExternalHandlers } from '../../../../services/actions/navigation/useExternalHandlers'
import { useAppHandlers } from '../../../../services/actions/app/useAppHandlers'
import Text from '../../../Text'
import Button from '../../../Button'
import SvgFragment from '../../../../fragments/SvgFragment'
import styled from 'styled-components'
import { Modal } from 'react-bootstrap'
import { styles } from '../../../../styles/themes/style'
import leadDeltaIcon from '../../../../assets/img/header/lead-delta-icon.png'

interface ISyncConnectionsModalProps {
    onClickHandler: () => void
}

const SyncConnectionsModal: FunctionComponent<ISyncConnectionsModalProps> = ({ onClickHandler }) => {
    const { openPermissionHandler } = useExternalHandlers()
    const { hideModal } = useAppHandlers()

    return (
        <>
            <StyledHeader>
                <HeaderBrand>
                    <BrandIcon src={leadDeltaIcon} />
                    <BrandName>LeadDelta</BrandName>
                </HeaderBrand>
                <CloseButton onClick={hideModal}>
                    <SvgFragment type="close" />
                </CloseButton>
            </StyledHeader>

            <StyledBody>
                <TitleSection>
                    <Text $heading2 $black>Before we sync your network</Text>
                    <Text $lighter>LeadDelta needs your permission to sync the following into your workspace.</Text>
                </TitleSection>

                <FeatureCard>
                    <IconWrapper>
                        <SvgFragment type="peopleBlue" />
                    </IconWrapper>
                    <CardContent>
                        <CardTitleRow>
                            <Text $bold $black>Sync your connections</Text>
                            <RequiredBadge>Required</RequiredBadge>
                        </CardTitleRow>
                        <Text $lighter>We import your 1st-degree LinkedIn connections so you can tag, filter, note, and organize them in LeadDelta and on a LinkedIn sidebar.</Text>
                    </CardContent>
                </FeatureCard>

                <FeatureCard>
                    <IconWrapper>
                        <SvgFragment type="inboxBlue" />
                    </IconWrapper>
                    <CardContent>
                        <CardTitleRow>
                            <Text $bold $black>Sync your inbox</Text>
                            <RequiredBadge>Required</RequiredBadge>
                        </CardTitleRow>
                        <Text $lighter>We import your LinkedIn messages so you can run your inbox like a CRM — tag, pin, archive, add notes and reminders, and use keyboard shortcuts to hit inbox zero faster.</Text>
                    </CardContent>
                </FeatureCard>

                <PrivacyRow>
                    <SvgFragment type="lock" />
                    <PrivacyText>
                        You can disconnect LeadDelta or remove your data at any time.{' '}
                        <SmallActionText onClickHandler={openPermissionHandler}>See exactly what LeadDelta can access.</SmallActionText>
                    </PrivacyText>
                </PrivacyRow>
            </StyledBody>

            <StyledFooter>
                <NotNowButton onClick={hideModal}>Not now</NotNowButton>
                <Button $type="blue" $bigButton onClickHandler={() => { onClickHandler(); hideModal(); }}>Sync my network</Button>
            </StyledFooter>
        </>
    )
}

const StyledHeader = styled(Modal.Header)`
    border-bottom: none;
    padding: 24px 24px 0 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
`

const HeaderBrand = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

const BrandIcon = styled.img`
    width: 32px;
    height: 32px;
`

const BrandName = styled.span`
    font-family: NunitoSans;
    font-size: 18px;
    font-weight: 700;
    color: ${({ theme: { primaryColor } }) => primaryColor};
`

const CloseButton = styled.div`
    cursor: pointer;
    position: absolute;
    right: 20px;
    & .svg { height: 24px; width: 24px; }
`

const StyledBody = styled(Modal.Body)`
    padding: 20px 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`

const TitleSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`

const FeatureCard = styled.div`
    border: 1px solid ${({ theme: { quinaryColor } }) => quinaryColor};
    border-radius: 8px;
    padding: 16px;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 12px;
`

const IconWrapper = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    margin-top: 2px;
    & .svg { width: 24px; height: 24px; }
`

const CardContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
`

const CardTitleRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`

const RequiredBadge = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${styles.colors.black400};
    background-color: ${({ theme: { primary } }) => primary ? '#F0F2F5' : styles.colors.darkblack300};
    padding: 3px 10px;
    border-radius: 100px;
    white-space: nowrap;
`

const PrivacyRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;

    & .svg {
        width: 17px;
        height: 18px;
        flex-shrink: 0;
        margin-top: 2px;
    }
`

const SmallActionText = styled(ActionText)`
    font-size: 10px;
`

const PrivacyText = styled.p`
    font-size: 12px;
    color: ${({ theme: { nonaryColor } }) => nonaryColor};
    margin: 0;
    flex: 1;
    line-height: 1.5;
`

const NotNowButton = styled.button`
    background: none;
    border: none;
    padding: 8px 16px;
    font-family: NunitoSans;
    font-size: 14px;
    font-weight: 700;
    color: ${({ theme: { nonaryColor } }) => nonaryColor};
    cursor: pointer;
    border-radius: 4px;

    &:hover {
        color: ${({ theme: { primaryColor } }) => primaryColor};
        background-color: ${({ theme: { senaryColor } }) => senaryColor};
    }
`

const StyledFooter = styled(Modal.Footer)`
    border-top: none;
    padding: 0 24px 24px 24px;
    display: flex !important;
    flex-wrap: nowrap !important;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    & button { margin: 0; }
`

export default SyncConnectionsModal
